import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from "@clerk/clerk-react";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { CallWithOutAuth } from '../../../actions/apiAction';
import { ANONYMOUS_SESSION_URL, FUNNEL_ASSIGNMENT_URL, GET_PUBLIC_PROFILE_URL } from '../../../utils/apiUrl';
import { getDeviceKey, saveAnonymousSession, saveFunnelAssignment } from '../../../utils/anonymousSession';
import { SocketConnect } from '../../../socket';
import { Avatar } from '@mui/material';
import facebookImage from '../../../assets/images/facebook.svg';
import appleImage from '../../../assets/images/apple.svg';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneInput from 'react-phone-input-2';
import BrandLogo from '../../../components/BrandLogo';
import 'react-phone-input-2/lib/style.css';
import VideoModal from '../../../components/Modals/OnboardingModal';
import SimplifiedAuthCard from '../../../components/Auth/SimplifiedAuthCard';
import { trackEvent } from '../../../utils/analytics';
import Cookie from '../../../utils/Cookie';

const ChatLanding = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const { isSignedIn, isLoaded } = useAuth();
    const [userDetails, setUserDetails] = useState(null)
    const [showOnboarding, setShowOnboarding] = useState(
        () => Cookie.getCookie('isOnbordingShown') !== 'true'
    );
    const [profileStatus, setProfileStatus] = useState('loading');
    const [trialBlocked, setTrialBlocked] = useState(false);
    const [trialError, setTrialError] = useState('');
    const [funnelVariant, setFunnelVariant] = useState(null);
    const videoOpenTracked = useRef(false);
    const trialStarted = useRef(false);
    const landingPromptTracked = useRef(false);

    useEffect(() => {
        // isSignedIn arrives after Clerk isLoaded. Without it in the
        // deps, a signed-in visit to /:username fetches the profile once,
        // misses the redirect, and stays on the loading spinner.
        if (isLoaded) {
            getUserDetails();
        }
    }, [isLoaded, isSignedIn]);

    const getUserDetails = async () => {
        setProfileStatus('loading');
        const response = await CallWithOutAuth("GET", GET_PUBLIC_PROFILE_URL(username));
        if (response?.status == 200 && response?.data) {
            setUserDetails(response.data)
            setProfileStatus('found')
            if (isSignedIn) {
                navigate(`/user/creator-list?username=${username}`);
            }
            return
        }
        setProfileStatus('missing')
        navigate('/creator-not-found/' + encodeURIComponent(username), { replace: true })
    }

    const finishSplash = () => {
        Cookie.setCookie('isOnbordingShown', 'true', 360);
        setShowOnboarding(false);
        trackEvent('onboarding_video_complete', { username });

    };

    const enterTrial = async () => {
        if (trialStarted.current) return;
        trialStarted.current = true;
        setTrialError('');
        const response = await CallWithOutAuth('POST', ANONYMOUS_SESSION_URL, {
            creator_username: username,
            device_key: getDeviceKey() || undefined,
        });
        const data = response?.data;
        if (data?.variant === 'A' && !data?.token) {
            saveFunnelAssignment(data);
            setFunnelVariant('A');
            return;
        }
        if (response?.status === 409 || data?.error === 'trial_claimed') {
            setTrialBlocked(true);
            return;
        }
        if (response?.status !== 200 || !data?.token || !data?.creator_id) {
            trialStarted.current = false;
            setTrialError('Could not open the chat. Try again.');
            return;
        }
        saveFunnelAssignment(data);
        saveAnonymousSession(data);
        Cookie.setCookie('activeChatId', data.chat_id, 1);
        SocketConnect(() => Promise.resolve(data.token));
        navigate(`/user/chat?id=${encodeURIComponent(data.creator_id)}&username=${encodeURIComponent(username)}`);
    };

    useEffect(() => {
        if (!isLoaded || isSignedIn || profileStatus !== 'found') return;
        let cancelled = false;
        (async () => {
            const response = await CallWithOutAuth('POST', FUNNEL_ASSIGNMENT_URL, {
                device_key: getDeviceKey() || undefined,
            });
            const data = response?.data;
            if (cancelled) return;
            if (response?.status === 200 && (data?.variant === 'A' || data?.variant === 'B')) {
                saveFunnelAssignment(data);
                setFunnelVariant(data.variant);
                return;
            }
            setFunnelVariant('A');
        })();
        return () => { cancelled = true; };
    }, [isLoaded, isSignedIn, profileStatus]);

    useEffect(() => {
        if (funnelVariant !== 'B') return;
        if (!isLoaded || isSignedIn || profileStatus !== 'found' || trialBlocked) return;
        if (userDetails?.splash_video_url && showOnboarding) return;
        enterTrial();
    }, [funnelVariant, isLoaded, isSignedIn, profileStatus, showOnboarding, trialBlocked, userDetails, username]);

    useEffect(() => {
        if (landingPromptTracked.current) return;
        if (!isLoaded || isSignedIn || profileStatus !== 'found') return;
        if (userDetails?.splash_video_url && showOnboarding) return;
        if (!trialBlocked && funnelVariant !== 'A') return;
        landingPromptTracked.current = true;
        trackEvent('registration_prompt_shown', {
            surface: trialBlocked ? 'trial_claimed' : 'landing',
            username,
        });
    }, [isLoaded, isSignedIn, profileStatus, showOnboarding, trialBlocked, funnelVariant, userDetails, username]);

    useEffect(() => {
        // Cookie means they already watched this splash. Stay on /:username
        // and show the magic-link form. Do not send them to /login — that
        // drops the creator username and loops with the Clerk session.
        if (Cookie.getCookie('isOnbordingShown') === 'true') {
            setShowOnboarding(false);
            return;
        }
        if (!videoOpenTracked.current) {
            videoOpenTracked.current = true;
            trackEvent('onboarding_video_open', { username });
        }
    }, [username])
    if (!isLoaded || isSignedIn || profileStatus !== 'found') {
        return (
            <div className="chat-landing-page">
                <div className="chat-landing-loading">
                    <div className="spin-ring">
                        <div className="ring"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>Chat Onboarding</title>
            </Helmet>

            <div className="chat-landing-page">
                {(userDetails?.splash_video_url && showOnboarding) ? <div className="chatpage-main-div">
                    <VideoModal
                        open={showOnboarding}
                        onClose={finishSplash}
                        videoSrc={userDetails?.splash_video_url}
                        ctaLabel={"Continue"}
                        onComplete={() => console.log('done')}
                    />
                </div>
                : (trialBlocked || funnelVariant === 'A') ?
                <div className="chat-landing-inner">
                    <div className="chat-landing-creator">
                        <div className="avatar-frame">
                            <Avatar alt={userDetails?.first_name} src={userDetails?.profile_picture_url}>
                                {userDetails?.first_name?.charAt(0)}
                            </Avatar>
                        </div>
                        <h1>{userDetails?.first_name} {userDetails?.last_name}</h1>
                        <p>{userDetails?.description}</p>
                    </div>

                    <SimplifiedAuthCard
                        initialMode="signup"
                        redirectUsername={username}
                        redirectComplete={`/user/creator-list?username=${username}`}
                        submitLabel={`Chat with ${userDetails?.first_name || "creator"}`}
                    />

                    <div className="chat-landing-footer">
                        © 2026, Syntimacy LLC
                    </div>
                </div>
                :
                <div className="chat-landing-loading">
                    <div className="spin-ring">
                        <div className="ring"></div>
                    </div>
                    {trialError && (
                        <button type="button" onClick={() => { trialStarted.current = false; enterTrial(); }}>
                            {trialError}
                        </button>
                    )}
                </div>}
            </div>
        </>
    );
}

export default ChatLanding;
